/**
 * PublicHeader — LE header public. Unique : toutes les pages publiques
 * routées le montent, sans exception. `siteV2/SiteHeaderV2` n'est plus
 * qu'un réexport de ce fichier (la home, /plans et /entreprises portaient
 * chacune leur propre barre, avec des entrées et des comportements qui
 * avaient commencé à diverger).
 *
 * Layout (per frontend/src/reference/header-jurisdiction-reference.html):
 *   Left   : Archer logo (→ /) + les 5 entrées de `navFor()`
 *   Right  : Lang toggle (FR|NL|EN) | separator | Jurisdiction pill → dropdown
 *            | « Se connecter » | CTA « Commencer gratuitement »
 *   <820px : tiroir SiteMobileMenu — mêmes entrées, même sélecteur de langue
 *
 * Jurisdiction dropdown:
 *   - Belgium = LIVE, active (only clickable option).
 *   - France / US / UAE = "COMING SOON · 2027", disabled.
 *   - This is the visible side of the post-M6 "freeze US" decision: US isn't
 *     offered as an active jurisdiction until M6+.
 *
 * Language switch writes to the shared landingTranslations locale store.
 * Since BE is the only active jurisdiction, all 3 languages map to `be-<lang>`.
 * Authenticated users also get the new `language` pushed to their profile.
 *
 * Self-contained — parents can still observe changes via onLanguageChange /
 * onJurisdictionChange callbacks for backward compatibility.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { seedLanguageFromUser } from '../hooks/useAppLanguage';
import LanguageSwitch, { useLanguageChoice } from './LanguageSwitch';
import SiteMobileMenu from './siteV2/SiteMobileMenu';
import { navFor } from './siteV2/routes';
import useStartRoute from '../hooks/useStartRoute';
import '../styles/header-v2.css';

// Copie d'auth de la nav. Les LIBELLÉS DES 5 ENTRÉES ne sont plus ici :
// ils viennent de `navFor()` (siteV2/routes.js), source unique partagée
// avec le tiroir mobile et la colonne « Produit » du footer. Les avoir en
// double, c'était la garantie qu'un jour les trois divergent.
const NAV_COPY = {
  fr: {
    signIn: 'Se connecter',
    cta: 'Commencer gratuitement',
    myDashboard: 'Mon Dashboard',
  },
  nl: {
    signIn: 'Inloggen',
    cta: 'Gratis beginnen',
    myDashboard: 'Mijn Dashboard',
  },
  en: {
    signIn: 'Sign in',
    cta: 'Get started free',
    myDashboard: 'My Dashboard',
  },
};

// Jurisdiction dropdown copy per UI language.
const JURISDICTION_COPY = {
  fr: {
    currentName: 'Droit belge',
    headerTitle: 'Législation applicable',
    headerSub: 'Choisissez votre juridiction',
    sectionAvailable: 'Disponible',
    comingSoon: 'Prochaines juridictions',
    comingSoonTag: 'COMING SOON',
    footer: 'Chaque juridiction utilise son propre corpus légal vérifié',
    options: {
      BE: { name: 'Belgique',            sub: 'Droit belge · Code civil · Code judiciaire' },
      FR: { name: 'France',              sub: 'Code civil · Code du travail' },
      US: { name: 'États-Unis',          sub: 'Federal Law · 50 State Laws' },
      AE: { name: 'Émirats Arabes Unis', sub: 'UAE Federal Law · DIFC · ADGM' },
    },
  },
  nl: {
    currentName: 'Belgisch recht',
    headerTitle: 'Toepasselijke wetgeving',
    headerSub: 'Kies uw rechtsgebied',
    sectionAvailable: 'Beschikbaar',
    comingSoon: 'Volgende rechtsgebieden',
    comingSoonTag: 'COMING SOON',
    footer: 'Elk rechtsgebied gebruikt zijn eigen geverifieerd juridisch corpus',
    options: {
      BE: { name: 'België',                  sub: 'Belgisch recht · Burgerlijk Wetboek · Gerechtelijk Wetboek' },
      FR: { name: 'Frankrijk',               sub: 'Burgerlijk Wetboek · Arbeidsrecht' },
      US: { name: 'Verenigde Staten',        sub: 'Federal Law · 50 State Laws' },
      AE: { name: 'Verenigde Arabische Emiraten', sub: 'UAE Federal Law · DIFC · ADGM' },
    },
  },
  en: {
    currentName: 'Belgian law',
    headerTitle: 'Applicable legislation',
    headerSub: 'Choose your jurisdiction',
    sectionAvailable: 'Available',
    comingSoon: 'Upcoming jurisdictions',
    comingSoonTag: 'COMING SOON',
    footer: 'Each jurisdiction uses its own verified legal corpus',
    options: {
      BE: { name: 'Belgium',              sub: 'Belgian law · Civil Code · Judicial Code' },
      FR: { name: 'France',               sub: 'Civil Code · Labor Code' },
      US: { name: 'United States',        sub: 'Federal Law · 50 State Laws' },
      AE: { name: 'United Arab Emirates', sub: 'UAE Federal Law · DIFC · ADGM' },
    },
  },
};

// Inline balance-scale icon (brief spec).
function BalanceIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v18" />
      <path d="M4 7h16" />
      <path d="M2 13h4" />
      <path d="M18 13h4" />
      <path d="M4 7L2 13" />
      <path d="M4 7l2 6" />
      <path d="M20 7l-2 6" />
      <path d="M20 7l2 6" />
      <path d="M9 21h6" />
    </svg>
  );
}

function ChevronDown({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}

/**
 * `authCta="dashboard"` (page d'accueil) : la zone d'authentification se
 * réduit à UN bouton « Mon Dashboard », quel que soit l'état de la session
 * — c'est la barre de la maquette de la home. Connecté, il mène au
 * dashboard ; déconnecté (ou session pas encore tranchée), à la connexion.
 * Sans la prop, rien ne change : « Se connecter » + CTA, ou « Mon
 * Dashboard ».
 */
export default function PublicHeader({ onLanguageChange, onJurisdictionChange, authCta }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, loading: authLoading } = useAuth();
  // Le CTA principal du site. Une seule décision, prise ailleurs :
  // cf. hooks/useStartRoute.js.
  const startRoute = useStartRoute();

  // La langue ne vit plus dans un `useState` local : elle vient du store
  // partagé (`useSiteLanguage`), celui-là même que lisent le footer et le
  // corps de chaque page publique. Le header en était devenu le seul
  // propriétaire — il changeait ses propres libellés et laissait le reste
  // de la page en français, ce que l'utilisateur voyait comme « rien ne se
  // passe quand je change la langue ».
  //
  // `useLanguageChoice` porte les DEUX gestes de l'écriture (store local +
  // report au compte) : ils sont désormais au même endroit que ceux du
  // sélecteur de l'app connectée et du `<select>` des Paramètres.
  const [language, chooseLanguage] = useLanguageChoice();

  const [jurisdictionOpen, setJurisdictionOpen] = useState(false);
  const pillRef = useRef(null);

  // Sync with user profile once auth resolves — mais SEULEMENT si cet
  // appareil n'a encore aucun choix de langue.
  //
  // Cet effet faisait l'inverse : la langue du compte écrasait le store
  // local dès que `/auth/me` répondait. Conséquence, sur la page de
  // connexion qui monte ce header : un visiteur qui choisissait NL sur le
  // site, puis se connectait à un compte enregistré en `fr`, se voyait
  // renvoyé en français une fraction de seconde après le login — le choix
  // qu'il venait de faire était annulé par une valeur posée à
  // l'inscription. C'est exactement le trajet qu'un néerlandophone fait.
  //
  // La règle est désormais la même partout (cf. `seedLanguageFromUser`) :
  // le compte AMORCE un appareil vierge, il ne CORRIGE pas un choix récent.
  // Le sens inverse — propager un choix vers le compte — est le rôle du
  // sélecteur des Paramètres, qui écrit `PUT /api/profile`.
  useEffect(() => {
    if (!user) return;
    seedLanguageFromUser(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Notify parent callbacks for backward compatibility with HomePage etc.
  useEffect(() => {
    onLanguageChange && onLanguageChange(language);
    onJurisdictionChange && onJurisdictionChange('BE');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!jurisdictionOpen) return;
    const handler = (e) => {
      if (pillRef.current && !pillRef.current.contains(e.target)) {
        setJurisdictionOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [jurisdictionOpen]);

  // `chooseLanguage` écrit `be-<lang>` dans le store ET émet
  // LOCALE_CHANGE_EVENT : le header, le footer et le corps de la page se
  // re-rendent ensemble, sur la même valeur. Il pousse aussi la langue au
  // compte quand quelqu'un est connecté. Le tiroir mobile reçoit ce même
  // gestionnaire — il envoie 'FR', que `normalizeLang` ramène à 'fr'.
  const handleLanguageChange = chooseLanguage;

  const navCopy = NAV_COPY[language] || NAV_COPY.fr;
  const jdCopy = JURISDICTION_COPY[language] || JURISDICTION_COPY.fr;
  const navItems = navFor(language);

  const handleNav = (path) => {
    setJurisdictionOpen(false);
    navigate(path);
  };

  // Entrée active : le chemin courant, ou l'une de ses variantes. Les
  // alias de /attorneys et le préfixe de langue de /how-it-works mènent
  // à la même page — la marquer active seulement sur l'URL canonique
  // laisserait la nav muette là où l'utilisateur se trouve pourtant.
  const isActive = (to) => {
    if (pathname === to) return true;
    if (to === '/attorneys') return ['/ton-equipe', '/qui-se-bat-pour-toi'].includes(pathname);
    if (to === '/how-it-works') return /^\/[a-z]{2}\/how-it-works$/.test(pathname);
    if (to === '/plans') return pathname === '/pricing';
    return false;
  };

  return (
    <nav className="public-header-v2" data-testid="public-header">
      <div className="header-left">
        <div
          className="header-logo"
          onClick={() => handleNav('/')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') handleNav('/'); }}
          data-testid="public-header-logo"
        >
          <img src="/logos/archer-logo-wordmark.svg" alt="Archer" />
        </div>

        {/* Les cinq entrées, rendues depuis `navFor()`. Ni Blog, ni FAQ,
            ni « Ce qu'on traite », ni « L'ancien monde vs Archer » : leurs
            routes répondent toujours en accès direct, elles ne sont
            simplement plus liées d'ici. */}
        <div className="header-nav">
          {navItems.map(({ key, label, to, testId }) => (
            <a
              key={key}
              onClick={() => handleNav(to)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handleNav(to); }}
              className={isActive(to) ? 'active' : undefined}
              aria-current={isActive(to) ? 'page' : undefined}
              data-testid={`public-header-${testId}`}
            >
              {label}
            </a>
          ))}
        </div>
      </div>

      <div className="header-right">
        {/* Language toggle — même composant que le châssis de l'app. Le
            header lui passe SA langue et SON gestionnaire : il tient déjà
            l'une pour ses libellés de nav et alimente le tiroir mobile
            avec l'autre, une seconde lecture du store ici ferait deux
            abonnements dans une seule barre. */}
        <LanguageSwitch
          language={language}
          onChange={handleLanguageChange}
          testIdPrefix="public-header-lang"
        />

        <div className="header-sep" aria-hidden="true" />

        {/* Jurisdiction pill */}
        <div
          className={`jurisdiction-pill${jurisdictionOpen ? ' open' : ''}`}
          onClick={() => setJurisdictionOpen(v => !v)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') setJurisdictionOpen(v => !v); }}
          aria-haspopup="listbox"
          aria-expanded={jurisdictionOpen}
          ref={pillRef}
          data-testid="public-header-jurisdiction-pill"
        >
          <div className="jurisdiction-icon">
            <BalanceIcon />
          </div>
          <span className="jurisdiction-flag" aria-hidden="true">🇧🇪</span>
          <span className="jurisdiction-name">{jdCopy.currentName}</span>
          <span className="jurisdiction-live" aria-hidden="true" />
          <ChevronDown className="jurisdiction-arrow" />

          {jurisdictionOpen && (
            <div
              className="jurisdiction-dropdown"
              role="listbox"
              onClick={(e) => e.stopPropagation()}
              data-testid="public-header-jurisdiction-dropdown"
            >
              <div className="jd-header">
                <div className="jd-header-icon">
                  <BalanceIcon />
                </div>
                <div className="jd-header-body">
                  <div className="jd-header-title">{jdCopy.headerTitle}</div>
                  <div className="jd-header-sub">{jdCopy.headerSub}</div>
                </div>
              </div>

              <div className="jd-section">{jdCopy.sectionAvailable}</div>

              <button
                type="button"
                role="option"
                aria-selected="true"
                className="jd-option active"
                data-testid="public-header-jurisdiction-be"
              >
                <span className="jd-option-flag" aria-hidden="true">🇧🇪</span>
                <div className="jd-option-body">
                  <div className="jd-option-name">{jdCopy.options.BE.name}</div>
                  <div className="jd-option-sub">{jdCopy.options.BE.sub}</div>
                </div>
                <span className="jd-badge live">LIVE</span>
              </button>

              <div className="jd-sep" />

              <div className="jd-coming-banner">
                <span className="jd-coming-banner-icon" aria-hidden="true">🚀</span>
                <span className="jd-coming-banner-text">{jdCopy.comingSoon}</span>
                <span className="jd-coming-banner-tag">{jdCopy.comingSoonTag}</span>
              </div>

              {['FR', 'US', 'AE'].map((code) => (
                <div
                  key={code}
                  className="jd-option disabled"
                  role="option"
                  aria-selected="false"
                  aria-disabled="true"
                  data-testid={`public-header-jurisdiction-${code.toLowerCase()}`}
                >
                  <span className="jd-option-flag" aria-hidden="true">
                    {code === 'FR' ? '🇫🇷' : code === 'US' ? '🇺🇸' : '🇦🇪'}
                  </span>
                  <div className="jd-option-body">
                    <div className="jd-option-name">{jdCopy.options[code].name}</div>
                    <div className="jd-option-sub">{jdCopy.options[code].sub}</div>
                  </div>
                  <span className="jd-badge soon">2027</span>
                </div>
              ))}

              <div className="jd-footer">
                <InfoIcon />
                {jdCopy.footer}
              </div>
            </div>
          )}
        </div>

        {/* Auth area.
            Tant que la session n'est pas tranchée, ni « Se connecter » ni
            « Mon Dashboard » : un placeholder invisible réserve la largeur
            exacte du bouton, donc aucun flash ni saut de mise en page. Le
            CTA reste affiché. Comportement repris de SiteHeaderV2, que ce
            header remplace sur la home et /plans — sans lui, ces deux
            pages auraient perdu leur anti-flash. */}
        {authCta === 'dashboard' ? (
          <button
            type="button"
            className="header-cta"
            onClick={() => handleNav(!authLoading && user ? '/dashboard' : '/login')}
            data-testid="public-header-dashboard-btn"
          >
            {navCopy.myDashboard}
          </button>
        ) : authLoading ? (
          <>
            <button type="button" className="header-signin" aria-hidden="true" tabIndex={-1}
                    style={{ visibility: 'hidden' }}>
              {navCopy.signIn}
            </button>
            <button
              type="button"
              className="header-cta"
              onClick={() => handleNav(startRoute)}
              data-testid="public-header-signup-btn"
            >
              {navCopy.cta}
            </button>
          </>
        ) : user ? (
          <button
            type="button"
            className="header-cta"
            onClick={() => handleNav('/dashboard')}
            data-testid="public-header-dashboard-btn"
          >
            {navCopy.myDashboard}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="header-signin"
              onClick={() => handleNav('/login')}
              data-testid="public-header-login-btn"
            >
              {navCopy.signIn}
            </button>
            <button
              type="button"
              className="header-cta"
              onClick={() => handleNav(startRoute)}
              data-testid="public-header-signup-btn"
            >
              {navCopy.cta}
            </button>
          </>
        )}

        {/* Sous 820px, `.header-nav` ET `.lang-switch` sont masqués par
            header-v2.css : le tiroir partagé est le seul accès aux entrées
            de nav, et désormais au choix de langue. On lui passe le même
            état et le même handler que le contrôle desktop — mêmes trois
            langues, même persistance. */}
        <SiteMobileMenu
          language={language}
          onLanguageChange={handleLanguageChange}
          nav={navItems}
          copy={navCopy}
        />
      </div>
    </nav>
  );
}
